import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Zap, Server } from "lucide-react";
import ScrollLink from "@/components/ScrollLink";

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden gradient-hero">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDY2RkYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" aria-hidden="true" />
      
      <div className="container-custom relative py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100">
              <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" aria-hidden="true" />
              <span className="text-sm font-medium text-foreground">99.99% Uptime Guaranteed</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
              Best BDIX Hosting Provider in Bangladesh
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-xl">
              Experience lightning-fast BDIX web hosting with affordable hosting prices in BD. NVMe SSD storage, enterprise-grade security, and 24/7 expert support. Fast BDIX hosting Bangladesh users trust.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button variant="gradient" size="xl" asChild>
                <ScrollLink to="/bdix-web-hosting">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </ScrollLink>
              </Button>
              <Button variant="outline" size="xl" asChild>
                <a href="https://my.webhostbuybd.com">Client Area</a>
              </Button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div>
                <div className="text-3xl font-bold text-foreground">500+</div>
                <div className="text-sm text-muted-foreground">Active Websites</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">99.99%</div>
                <div className="text-sm text-muted-foreground">Uptime Guaranteed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground">24/7</div>
                <div className="text-sm text-muted-foreground">Expert Support</div>
              </div>
            </div>
          </div>

          {/* Visual Element */}
          <div className="relative hidden lg:block animate-fade-in-up animation-delay-200" aria-hidden="true">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center">
                    <Server className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">BDIX Enterprise Server</div>
                    <div className="text-sm text-muted-foreground">Active & Running in Bangladesh</div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">CPU Usage</span>
                    <span className="text-sm font-medium text-foreground">23%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full w-[23%] gradient-primary rounded-full" />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Memory</span>
                    <span className="text-sm font-medium text-foreground">45%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full w-[45%] gradient-primary rounded-full" />
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">NVMe Storage</span>
                    <span className="text-sm font-medium text-foreground">62%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full w-[62%] gradient-primary rounded-full" />
                  </div>
                </div>
              </div>

              {/* Floating Cards */}
              <div className="absolute -top-4 -right-4 bg-card rounded-xl shadow-lg p-4 border border-border animate-pulse-glow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">SSL Secured</div>
                    <div className="text-xs text-muted-foreground">256-bit encryption</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-card rounded-xl shadow-lg p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">BDIX LiteSpeed</div>
                    <div className="text-xs text-muted-foreground">20x faster hosting</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;